/**
 * Sanitizer for on-site search terms before they enter analytics.
 *
 * Search boxes are free text, so a visitor can paste anything into one. This is
 * the single gate every search event goes through: it redacts the two patterns
 * that could re-identify a person (an email address, a long digit run such as a
 * phone number, house number, or postal code), collapses case so terms
 * aggregate, and caps length so an accidental paste can never ship a document
 * into an event property.
 *
 * Deliberately narrower than replay masking (lib/posthog-masking.ts), which
 * passes search inputs through unmasked: this makes the same already-visible
 * text queryable, it does not add a new category of collection.
 */

const MAX_QUERY_CHARS = 80;
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
const LONG_DIGIT_RUN = /\d{3,}/g;

export function sanitizeSearchQuery(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const cleaned = raw
    .replace(/\p{Cc}/gu, ' ')
    .replace(EMAIL_PATTERN, '[email]')
    .replace(LONG_DIGIT_RUN, '[number]')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!cleaned) return undefined;
  return cleaned.slice(0, MAX_QUERY_CHARS);
}
